import * as Sentry from '@sentry/react';
import oneWayEducation from './assets/images/oneWayEducation.png';

const EXCEL_CONSTANTS = {
  ROW_HEIGHTS: {
    COLUMN_NUMBERING: 30,
    IMAGE_ROW: 70,
    BOOKS_ROW: 90,
    ASSIGNMENTS_ROW: 70,
    STUDENT_HEADERS: 40,
    DATA_ROW: 25,
  },
  COLUMN_WIDTHS: {
    FIRST_COLUMN: 35,
    SECOND_THIRD_COLUMN: 20,
    FOURTH_COLUMN: 15,
    DEFAULT_MIN: 25,
    DEFAULT_MAX: 60,
    ASSIGNMENT_MAX_LENGTH: 50,
  },
  COLORS: {
    WHITE: 'FFFFFFFF',
    LIGHT_GRAY: 'FFD9D9D9',
    LIGHT_GRAY_ALT: 'FFF3F2F3',
    LIGHT_BLUE: 'FFDEE6F1',
    LIGHT_PINK: 'FFEEDDDC',
    LIGHT_GREEN: 'FFECF2DF',
    BLACK: 'FF000000',
  },
  ROW_NUMBERS: {
    COLUMN_NUMBERING: 1,
    IMAGE: 2,
    BOOKS: 3,
    ASSIGNMENTS: 4,
    STUDENT_HEADERS: 5,
    FIRST_DATA_ROW: 6,
  },
  IMAGE_POSITION: {
    START_COL: 0.3,
    START_ROW: 1.2,
    END_COL: 2.7,
    END_ROW: 1.8,
  },
};

const SIMPLE_BORDER = {
  top: { style: 'thin', color: { argb: 'FFB0BEC5' } },
  bottom: { style: 'thin', color: { argb: 'FFB0BEC5' } },
  left: { style: 'thin', color: { argb: 'FFB0BEC5' } },
  right: { style: 'thin', color: { argb: 'FFB0BEC5' } },
};

const getBase64FromUrl = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const applyCellBorders = (cell) => {
  cell.border = {
    top: { style: 'thin', color: { argb: EXCEL_CONSTANTS.COLORS.BLACK } },
    left: { style: 'thin', color: { argb: EXCEL_CONSTANTS.COLORS.BLACK } },
    bottom: { style: 'thin', color: { argb: EXCEL_CONSTANTS.COLORS.BLACK } },
    right: { style: 'thin', color: { argb: EXCEL_CONSTANTS.COLORS.BLACK } },
  };
};

const applyCellFill = (cell, color) => {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: color },
  };
};

const applyCellAlignment = (cell, options = {}) => {
  cell.alignment = {
    horizontal: options.horizontal || 'center',
    vertical: options.vertical || 'middle',
    wrapText: options.wrapText !== false,
  };
};

const styleImageRow = (cell) => {
  cell.border = {};
  applyCellFill(cell, EXCEL_CONSTANTS.COLORS.WHITE);
  applyCellAlignment(cell);
  cell.font = { size: 12 };
};

const styleColumnNumberingRow = (cell, colNumber) => {
  applyCellBorders(cell);
  cell.font = { size: 10 };
  const fillColor =
    colNumber % 2 === 0 ? EXCEL_CONSTANTS.COLORS.LIGHT_GRAY : EXCEL_CONSTANTS.COLORS.LIGHT_GRAY_ALT;
  applyCellFill(cell, fillColor);
  applyCellAlignment(cell);
};

const styleBooksRow = (cell, colNumber) => {
  applyCellBorders(cell);
  const fillColor =
    colNumber >= 4 ? EXCEL_CONSTANTS.COLORS.LIGHT_BLUE : EXCEL_CONSTANTS.COLORS.LIGHT_GREEN;
  applyCellFill(cell, fillColor);
  cell.font = { bold: false, size: 11 };
  applyCellAlignment(cell);
};

const styleAssignmentsRow = (cell, colNumber) => {
  applyCellBorders(cell);
  const fillColor =
    colNumber >= 4 ? EXCEL_CONSTANTS.COLORS.LIGHT_PINK : EXCEL_CONSTANTS.COLORS.LIGHT_GREEN;
  applyCellFill(cell, fillColor);
  cell.font = { bold: false, size: colNumber >= 4 ? 11 : 12 };
  applyCellAlignment(cell);
};

const styleStudentHeadersRow = (cell, colNumber) => {
  applyCellFill(cell, EXCEL_CONSTANTS.COLORS.LIGHT_GREEN);
  cell.font = { bold: true, size: 11 };
  applyCellAlignment(cell);

  if (colNumber < 4) {
    cell.border = {
      top: { style: 'none' },
      left: { style: 'thin', color: { argb: EXCEL_CONSTANTS.COLORS.BLACK } },
      bottom: { style: 'thin', color: { argb: EXCEL_CONSTANTS.COLORS.BLACK } },
      right: { style: 'thin', color: { argb: EXCEL_CONSTANTS.COLORS.BLACK } },
    };
  } else {
    applyCellBorders(cell);
  }
};

const styleDataRow = (cell, rowNumber) => {
  applyCellBorders(cell);
  cell.font = { size: 10 };
  const rowOffset = rowNumber - EXCEL_CONSTANTS.ROW_NUMBERS.FIRST_DATA_ROW;
  const fillColor =
    rowOffset % 2 === 0 ? EXCEL_CONSTANTS.COLORS.WHITE : EXCEL_CONSTANTS.COLORS.LIGHT_GRAY_ALT;
  applyCellFill(cell, fillColor);
  applyCellAlignment(cell);
};

const calculateColumnWidths = (worksheet) => {
  worksheet.columns.forEach((column, index) => {
    let maxLength = 0;

    column.eachCell({ includeEmpty: true }, (cell, rowNumber) => {
      if (rowNumber === EXCEL_CONSTANTS.ROW_NUMBERS.IMAGE) return;

      const cellValue = cell.value ? cell.value.toString() : '';
      if (rowNumber === EXCEL_CONSTANTS.ROW_NUMBERS.ASSIGNMENTS && cellValue.length > 0) {
        maxLength = Math.max(
          maxLength,
          Math.min(cellValue.length, EXCEL_CONSTANTS.COLUMN_WIDTHS.ASSIGNMENT_MAX_LENGTH),
        );
      } else if (cellValue.length > 0) {
        maxLength = Math.max(maxLength, cellValue.length);
      }
    });

    if (index === 0) {
      column.width = Math.max(EXCEL_CONSTANTS.COLUMN_WIDTHS.FIRST_COLUMN, maxLength + 2);
    } else if (index === 1 || index === 2) {
      column.width = Math.max(EXCEL_CONSTANTS.COLUMN_WIDTHS.SECOND_THIRD_COLUMN, maxLength + 2);
    } else if (index === 3) {
      column.width = Math.max(EXCEL_CONSTANTS.COLUMN_WIDTHS.FOURTH_COLUMN, maxLength + 2);
    } else {
      column.width = Math.max(
        EXCEL_CONSTANTS.COLUMN_WIDTHS.DEFAULT_MIN,
        Math.min(maxLength + 4, EXCEL_CONSTANTS.COLUMN_WIDTHS.DEFAULT_MAX),
      );
    }
  });
};

const setRowHeights = (worksheet) => {
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === EXCEL_CONSTANTS.ROW_NUMBERS.COLUMN_NUMBERING) {
      row.height = EXCEL_CONSTANTS.ROW_HEIGHTS.COLUMN_NUMBERING;
    } else if (rowNumber === EXCEL_CONSTANTS.ROW_NUMBERS.IMAGE) {
      row.height = EXCEL_CONSTANTS.ROW_HEIGHTS.IMAGE_ROW;
    } else if (rowNumber === EXCEL_CONSTANTS.ROW_NUMBERS.BOOKS) {
      row.height = EXCEL_CONSTANTS.ROW_HEIGHTS.BOOKS_ROW;
    } else if (rowNumber === EXCEL_CONSTANTS.ROW_NUMBERS.ASSIGNMENTS) {
      row.height = EXCEL_CONSTANTS.ROW_HEIGHTS.ASSIGNMENTS_ROW;
    } else if (rowNumber === EXCEL_CONSTANTS.ROW_NUMBERS.STUDENT_HEADERS) {
      row.height = EXCEL_CONSTANTS.ROW_HEIGHTS.STUDENT_HEADERS;
    } else {
      row.height = EXCEL_CONSTANTS.ROW_HEIGHTS.DATA_ROW;
    }
  });
};

const applyCellStyling = (worksheet) => {
  worksheet.eachRow((row, rowNumber) => {
    row.eachCell((cell, colNumber) => {
      if (rowNumber === EXCEL_CONSTANTS.ROW_NUMBERS.IMAGE) {
        styleImageRow(cell);
      } else if (rowNumber === EXCEL_CONSTANTS.ROW_NUMBERS.COLUMN_NUMBERING) {
        styleColumnNumberingRow(cell, colNumber);
      } else if (rowNumber === EXCEL_CONSTANTS.ROW_NUMBERS.BOOKS) {
        styleBooksRow(cell, colNumber);
      } else if (rowNumber === EXCEL_CONSTANTS.ROW_NUMBERS.ASSIGNMENTS) {
        styleAssignmentsRow(cell, colNumber);
      } else if (rowNumber === EXCEL_CONSTANTS.ROW_NUMBERS.STUDENT_HEADERS) {
        styleStudentHeadersRow(cell, colNumber);
      } else {
        styleDataRow(cell, rowNumber);
      }
    });
  });
};

const addImageToWorksheet = async (workbook, worksheet) => {
  try {
    const base64Img = await getBase64FromUrl(oneWayEducation);
    const imageId = workbook.addImage({
      base64: base64Img,
      extension: 'png',
    });

    worksheet.mergeCells('A2:C2');
    worksheet.mergeCells('A3:C4');

    worksheet.addImage(imageId, {
      tl: {
        col: EXCEL_CONSTANTS.IMAGE_POSITION.START_COL,
        row: EXCEL_CONSTANTS.IMAGE_POSITION.START_ROW,
      },
      br: {
        col: EXCEL_CONSTANTS.IMAGE_POSITION.END_COL,
        row: EXCEL_CONSTANTS.IMAGE_POSITION.END_ROW,
      },
      editAs: 'oneCell',
    });
  } catch (error) {
    Sentry.captureException(`Error adding image to Excel: ${error}`);
  }
};

export async function writeGradebookMatrixExcelBuffer(data) {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Gradebook');
  worksheet.addRows(data);
  calculateColumnWidths(worksheet);
  setRowHeights(worksheet);
  applyCellStyling(worksheet);
  await addImageToWorksheet(workbook, worksheet);
  return workbook.xlsx.writeBuffer();
}

export async function downloadGradebookMatrixExcel(data, fileName) {
  const buffer = await writeGradebookMatrixExcelBuffer(data);
  const { saveAs } = await import('file-saver');
  let safeName = String(fileName ?? 'gradebook').trim();
  if (!/\.xlsx$/i.test(safeName)) {
    safeName = `${safeName.replace(/\.(csv|xls)?$/i, '')}.xlsx`;
  }
  saveAs(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    safeName,
  );
}

const escapeCsvField = (value) => {
  const s = value == null ? '' : String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

export const matrixToCsvString = (matrix) => {
  const rows = (matrix || []).map((row) => {
    const arr = Array.isArray(row) ? row : [];
    return arr.map(escapeCsvField).join(',');
  });
  return rows.join('\r\n');
};

export async function downloadGradebookMatrixCsv(data, fileName) {
  const text = matrixToCsvString(data);
  const { saveAs } = await import('file-saver');
  let safeName = String(fileName ?? 'gradebook').trim();
  if (!/\.csv$/i.test(safeName)) {
    safeName = `${safeName.replace(/\.(xlsx|xls)?$/i, '')}.csv`;
  }
  const blob = new Blob([`\uFEFF${text}`], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, safeName);
}

export async function writeSimpleMatrixExcelBuffer(matrix) {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Gradebook', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  (matrix || []).forEach((row, ri) => {
    const excelRow = sheet.addRow(Array.isArray(row) ? row : []);
    excelRow.height = ri === 0 ? 22 : 20;
    excelRow.eachCell((cell) => {
      cell.border = SIMPLE_BORDER;
      if (ri === 0) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF02796D' },
        };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      } else {
        const zebra = ri % 2 === 1 ? 'FFFFFFFF' : 'FFF5F5F5';
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: zebra } };
        cell.font = { size: 10, color: { argb: 'FF263238' } };
        cell.alignment = { vertical: 'middle', wrapText: true };
      }
    });
  });

  sheet.columns.forEach((col) => {
    let max = 12;
    col.eachCell({ includeEmpty: true }, (cell) => {
      const v = cell.value != null ? String(cell.value) : '';
      max = Math.max(max, Math.min(v.length, 55) + 2);
    });
    col.width = max;
  });

  return workbook.xlsx.writeBuffer();
}
